/**
 * Pointer and keyboard handling for the map (FD §3.3), the prototype's interactions on the new model:
 * - select: drag shapes to move them, drag dots to reshape (shared corners and junctions move together),
 *   double-click an edge to add a dot, right-click a dot to remove it, drag orange grips to change a road's width
 *   at one dot (Advanced); empty ground pans;
 * - road / river / habitat / lake / boundary: click points, double-click or Enter to finish, Esc to cancel;
 *   road points snap onto existing roads (yellow ring) and join there on a dot;
 * - icon: click to place; delete: click to remove; cut: click a road; join: click near a road end, then the
 *   other road (near its end: one road; along it: attach; same road: loop).
 * Scroll zooms around the pointer; the middle or right button drags the map.
 */
import { lerpAt, nearestOnPoly, type Derived } from '../../core/geom';
import { whyCantRemoveNode, whyCantRemoveVert } from '../../core/model';
import {
  addArea, addIcon, addRiver, addRoad, applyFollow, cutRoad, insertNode, insertRiverPoint, insertVert, joinRoads, moveIcon, moveNode,
  moveObject, moveRiverPoint, moveVert, prepareFollow, removeNode, removeRiverPoint, removeVert, setNodeWidth, setPlot, setRiverWidth,
  snapToRoads, type FollowCtx, type ObjectRef, type OpResult, type RoadSnapHit,
} from '../../core/ops';
import type { PlanDoc, Pt, RoadId } from '../../shared/types';
import { hitObject, hitTest, type Grip, type Handle } from '../render/hit';
import type { ToolPreview } from '../render/renderer';
import { makeTransform, toMap, zoomAround, type Transform } from '../render/view';
import type { EditorStore, Selection } from '../store/editor';
import { toast } from '../ui/shell/dialogs';
import { deleteObject, placeHabitat, speciesList, trimAround, trimMessage, type TrimResult } from './edits';

/** Road widths a person can set with the grips (FD §4). */
export const ROAD_W_MIN = 2;
export const ROAD_W_MAX = 30;
/** River widths a person can set with the grip. */
export const RIVER_W_MIN = 3;
export const RIVER_W_MAX = 60;
/** Pointer travel (px) before a press on a shape becomes a move. */
const DRAG_START_PX = 4;
/** Snap reach for road points (screen px). */
const SNAP_PX = 12;

type Gesture =
  | { kind: 'pan'; sx: number; sy: number; cx: number; cy: number; zoom: number }
  | { kind: 'press'; sx: number; sy: number; start: Pt; target: ObjectRef }
  | { kind: 'move'; start: Pt; target: ObjectRef; follow: FollowCtx | null; iconAt?: Pt }
  | { kind: 'handle'; handle: Handle; roadId: RoadId | null; riverId: string | null; follow: FollowCtx | null; moved: boolean }
  | { kind: 'width'; grip: Grip; roadId: RoadId | null; riverId: string | null; follow: FollowCtx | null };

/** Drawing tools: what a finished outline becomes. */
const DRAW_TOOLS = new Set(['road', 'river', 'habitat', 'lake', 'boundary']);

/** One map's pointer logic; the canvas component forwards events and reads `preview()` for drawing. */
export class MapController {
  private store: EditorStore;
  private w = 1;
  private h = 1;
  private gesture: Gesture | null = null;
  private points: Pt[] = [];
  private snaps: (RoadSnapHit | null)[] = [];
  private cursor: Pt | null = null;
  private hoverSnap: RoadSnapHit | null = null;
  private joinFrom: { roadId: RoadId; end: 'start' | 'end'; at: Pt } | null = null;
  private hover: Selection = null;
  private cutAt: { a: Pt; b: Pt } | null = null;
  /** Map point under the pointer (for the status line). */
  pointer: Pt | null = null;
  /** Called when something only the controller knows changed (preview, pointer). */
  onChange: () => void = () => undefined;

  constructor(store: EditorStore) {
    this.store = store;
  }

  /** Sets the canvas size in CSS pixels. */
  resize(w: number, h: number): void {
    this.w = Math.max(1, w);
    this.h = Math.max(1, h);
  }

  /** The current view transform. */
  transform(): Transform {
    const s = this.store.getState();
    return makeTransform(this.w, this.h, s.doc.size, s.view.zoom, s.view.cx, s.view.cy);
  }

  private pxm(): number {
    return 1 / this.transform().k;
  }

  private mapPt(e: { offsetX: number; offsetY: number }): Pt {
    return toMap(this.transform(), e.offsetX, e.offsetY);
  }

  /** Throws away an unfinished drawing or join (tool change, Esc). */
  resetTool(): void {
    this.points = [];
    this.snaps = [];
    this.cursor = null;
    this.hoverSnap = null;
    this.joinFrom = null;
    this.hover = null;
    this.cutAt = null;
    if (this.gesture && this.gesture.kind !== 'pan') this.store.cancelDrag();
    this.gesture = null;
    this.onChange();
  }

  /** True while points are being placed. */
  isDrawing(): boolean {
    return this.points.length > 0;
  }

  /** What to draw on top of the map for the active tool. */
  preview(): ToolPreview {
    const tool = this.store.getState().tool;
    const closed = tool === 'habitat' || tool === 'lake' || tool === 'boundary';
    return {
      points: this.points,
      cursor: this.points.length ? this.cursor : null,
      closed,
      curved: tool === 'road' || tool === 'river',
      snap: tool === 'road' && this.hoverSnap ? [this.hoverSnap.x, this.hoverSnap.y] : null,
      ring: tool === 'join' && this.joinFrom ? this.joinFrom.at : null,
      hoverRoad: (tool === 'cut' || tool === 'join') && this.hover?.kind === 'road' ? this.hover.id : null,
      hoverDelete: tool === 'delete' ? this.hover : null,
      cutAt: tool === 'cut' ? this.cutAt : null,
    };
  }

  /** Status-line hint for the active tool. */
  hint(): string {
    const s = this.store.getState();
    switch (s.tool) {
      case 'select':
        return s.selection
          ? 'Drag the dots to reshape, drag the shape to move it. Double-click the edge to add a dot, right-click a dot to remove it.'
          : 'Click something to select it. Drag empty ground to move around, scroll to zoom.';
      case 'road':
        return this.points.length ? 'Keep clicking to add points. Double-click or press Enter to finish, Esc to cancel.' : 'Click to start a path. A yellow ring means it joins the path there.';
      case 'habitat':
        return s.toolSpecies ? 'Click around the habitat outline. Double-click or press Enter to close it.' : 'First pick an animal above.';
      case 'lake': return 'Click around the lake outline. Double-click or press Enter to close it.';
      case 'river': return 'Click to place river points. Double-click or press Enter to finish.';
      case 'boundary': return 'Click around the new zoo boundary. Double-click or press Enter to close it.';
      case 'icon': return 'Click on the map to place it.';
      case 'delete': return 'Click something to remove it. Undo brings it back.';
      case 'cut': return 'Click a path to cut it in two. To remove a stretch, cut at both ends and delete the piece.';
      case 'join': return this.joinFrom
        ? 'Now click the other path: near its end to make one path, anywhere along it to join onto it.'
        : 'Click a path near the end you want to join.';
    }
  }

  // ---- pointer events ----

  pointerDown(e: PointerEvent, el: HTMLElement): void {
    el.focus({ preventScroll: true });
    try { el.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    const s = this.store.getState(), p = this.mapPt(e);
    const pan = () => { this.gesture = { kind: 'pan', sx: e.offsetX, sy: e.offsetY, cx: s.view.cx, cy: s.view.cy, zoom: s.view.zoom }; };
    if (e.button === 1) return pan();
    switch (s.tool) {
      case 'select': return this.selectDown(e, p, pan);
      case 'icon':
        if (e.button !== 0) return pan();
        this.store.apply(addIcon, { type: s.toolIcon, x: p[0], y: p[1] }, { label: 'Add icon' });
        return;
      case 'delete': {
        if (e.button !== 0) return pan();
        const h = hitObject(s.doc, s.derived, p[0], p[1], this.pxm());
        if (h && h.type === 'object') deleteObject(this.store, h.sel);
        else pan();
        this.hover = null;
        return;
      }
      case 'cut': {
        if (e.button !== 0) return pan();
        const h = hitObject(s.doc, s.derived, p[0], p[1], this.pxm(), 'road');
        if (!h || h.type !== 'object' || h.sel.kind !== 'road') return pan();
        const roadId = h.sel.id;
        const res = this.store.apply(cutRoad, { roadId, x: p[0], y: p[1] }, { label: 'Cut path' });
        if (!res.ok) toast(res.reason, 'error');
        else {
          this.store.select(null);
          this.store.say(res.value.length === 2 ? 'Path cut in two. Select a piece and press Delete to remove it.' : 'The loop is open now. Cut again and delete a piece to remove a stretch.');
        }
        this.cutAt = null;
        return;
      }
      case 'join': return this.joinDown(e, p, pan);
      default:
        if (DRAW_TOOLS.has(s.tool)) {
          if (e.button !== 0) return pan();
          if (s.tool === 'habitat' && !s.toolSpecies) { this.store.say('Pick an animal first, then draw its habitat.'); return; }
          if (e.detail >= 2) return; // the double-click finishes the drawing
          if (s.tool === 'road') {
            const snap = snapToRoads(s.doc, s.derived, p[0], p[1], SNAP_PX * this.pxm());
            this.points.push(snap ? [snap.x, snap.y] : p);
            this.snaps.push(snap);
          } else {
            this.points.push(p);
          }
          this.cursor = p;
          this.onChange();
        }
    }
  }

  private selectDown(e: PointerEvent, p: Pt, pan: () => void): void {
    const s = this.store.getState();
    const hit = hitTest(s.doc, s.derived, p[0], p[1], { pxm: this.pxm(), selection: s.selection, grips: s.advanced, handles: true });
    const sel = s.selection;
    if (hit?.type === 'grip') {
      if (e.button !== 0) return;
      const roadId = sel?.kind === 'road' ? sel.id : null, riverId = sel?.kind === 'river' ? sel.id : null;
      this.store.beginDrag(roadId ? 'Change path width' : 'Change river width');
      const follow = roadId && s.follow && hit.grip.nodeId ? prepareFollow(s.doc, roadId, 'width', hit.grip.nodeId) : null;
      this.gesture = { kind: 'width', grip: hit.grip, roadId, riverId, follow };
      return;
    }
    if (hit?.type === 'handle') {
      if (e.button === 2 || e.altKey) { this.removeDot(hit.handle); return; }
      if (e.button !== 0) return pan();
      const roadId = sel?.kind === 'road' ? sel.id : null, riverId = sel?.kind === 'river' ? sel.id : null;
      this.store.beginDrag(hit.handle.kind === 'vert' ? 'Move corner' : 'Move dot');
      const follow = roadId && s.follow ? prepareFollow(s.doc, roadId, 'move') : null;
      this.gesture = { kind: 'handle', handle: hit.handle, roadId, riverId, follow, moved: false };
      return;
    }
    if (e.button !== 0) return pan();
    if (hit?.type === 'object') {
      const t = hit.sel;
      if (t.kind === 'plot') return pan();
      this.store.select(t);
      this.gesture = { kind: 'press', sx: e.offsetX, sy: e.offsetY, start: p, target: { kind: t.kind, id: t.id } };
      return;
    }
    if (sel) this.store.select(null);
    pan();
  }

  private joinDown(e: PointerEvent, p: Pt, pan: () => void): void {
    if (e.button !== 0) return pan();
    const s = this.store.getState();
    const h = hitObject(s.doc, s.derived, p[0], p[1], this.pxm(), 'road');
    if (!h || h.type !== 'object' || h.sel.kind !== 'road') return pan();
    const road = s.doc.roads[h.sel.id];
    if (!road) return;
    if (!this.joinFrom) {
      if (road.closed) { this.store.say('A loop has no loose end. Click a path near the end you want to join first.'); return; }
      const a = s.doc.nodes[road.nodes[0]!], b = s.doc.nodes[road.nodes[road.nodes.length - 1]!];
      if (!a || !b) return;
      const end = Math.hypot(p[0] - a.x, p[1] - a.y) <= Math.hypot(p[0] - b.x, p[1] - b.y) ? 'start' : 'end';
      const at: Pt = end === 'start' ? [a.x, a.y] : [b.x, b.y];
      this.joinFrom = { roadId: h.sel.id, end, at };
      this.onChange();
      return;
    }
    const from = this.joinFrom;
    this.joinFrom = null;
    let trim: TrimResult | null = null;
    const res = this.store.apply((d) => {
      const r = joinRoads(d, { a: from.roadId, endA: from.end, b: h.sel.kind === 'road' ? h.sel.id : '', x: p[0], y: p[1] });
      if (r.ok && s.follow && d.roads[from.roadId]) trim = trimAround(d, [from.roadId], speciesList(this.store));
      return r;
    }, undefined, { label: 'Join paths' });
    if (!res.ok) { toast(res.reason, 'error'); this.onChange(); return; }
    const what = res.value === 'closed' ? 'Closed the path into a loop.' : res.value === 'merged' ? 'Joined the two paths into one.' : 'Joined the path end onto the other path.';
    this.store.select({ kind: 'road', id: from.roadId });
    this.store.say(`${what} ${trimMessage(trim)}`.trim());
  }

  private removeDot(h: Handle): void {
    const s = this.store.getState();
    let res: OpResult;
    if (h.kind === 'vert') {
      const why = whyCantRemoveVert(s.doc, h.id);
      if (why) { toast(why, 'error'); return; }
      res = this.store.apply(removeVert, { vertId: h.id }, { label: 'Remove corner' });
    } else if (h.kind === 'node') {
      const why = whyCantRemoveNode(s.doc, h.id);
      if (why) { toast(why, 'error'); return; }
      res = this.store.apply(removeNode, { nodeId: h.id }, { label: 'Remove dot' });
    } else {
      const sel = s.selection;
      if (sel?.kind !== 'river') return;
      res = this.store.apply(removeRiverPoint, { riverId: sel.id, index: h.index }, { label: 'Remove river point' });
    }
    if (!res.ok) toast(res.reason, 'error');
  }

  pointerMove(e: PointerEvent): void {
    const s = this.store.getState(), p = this.mapPt(e);
    this.pointer = p;
    const g = this.gesture;
    if (g?.kind === 'pan') {
      const t = makeTransform(this.w, this.h, s.doc.size, g.zoom, g.cx, g.cy);
      this.store.setView({ zoom: g.zoom, cx: g.cx - (e.offsetX - g.sx) / t.k, cy: g.cy - (e.offsetY - g.sy) / t.k });
      return;
    }
    if (g?.kind === 'press') {
      if (Math.hypot(e.offsetX - g.sx, e.offsetY - g.sy) < DRAG_START_PX) return;
      const t = g.target;
      const label = t.kind === 'area' ? 'Move area' : t.kind === 'road' ? 'Move path' : t.kind === 'river' ? 'Move river' : 'Move icon';
      this.store.beginDrag(label);
      const ic = t.kind === 'icon' ? s.doc.icons[t.id] : undefined;
      this.gesture = {
        kind: 'move', start: g.start, target: t,
        follow: t.kind === 'road' && s.follow ? prepareFollow(s.doc, t.id, 'move') : null,
        ...(ic ? { iconAt: [ic.x, ic.y] as Pt } : {}),
      };
    }
    const g2 = this.gesture;
    if (g2?.kind === 'move') {
      const dx = p[0] - g2.start[0], dy = p[1] - g2.start[1], t = g2.target, follow = g2.follow, iconAt = g2.iconAt;
      this.store.updateDrag((d) => {
        if (t.kind === 'icon' && iconAt) moveIcon(d, { iconId: t.id, x: iconAt[0] + dx, y: iconAt[1] + dy });
        else moveObject(d, { target: t, dx, dy });
        if (follow) applyFollow(d, follow);
      });
      return;
    }
    if (g2?.kind === 'handle') {
      const h = g2.handle, follow = g2.follow, riverId = g2.riverId;
      g2.moved = true;
      this.store.updateDrag((d) => {
        if (h.kind === 'vert') moveVert(d, { vertId: h.id, x: p[0], y: p[1] });
        else if (h.kind === 'node') moveNode(d, { nodeId: h.id, x: p[0], y: p[1] });
        else if (riverId) moveRiverPoint(d, { riverId, index: h.index, x: p[0], y: p[1] });
        if (follow) applyFollow(d, follow);
      });
      return;
    }
    if (g2?.kind === 'width') {
      const gr = g2.grip, half = Math.abs((p[0] - gr.mid[0]) * gr.nx + (p[1] - gr.mid[1]) * gr.ny);
      const lo = g2.roadId ? ROAD_W_MIN : RIVER_W_MIN, hi = g2.roadId ? ROAD_W_MAX : RIVER_W_MAX;
      const w = Math.min(hi, Math.max(lo, Math.round(half * 2 * 2) / 2));
      const follow = g2.follow, nodeId = gr.nodeId, riverId = g2.riverId;
      this.store.updateDrag((d) => {
        if (nodeId) setNodeWidth(d, { nodeId, w });
        else if (riverId) setRiverWidth(d, { riverId, w });
        if (follow) applyFollow(d, follow);
      });
      return;
    }
    // hover feedback
    const tool = s.tool;
    if (tool === 'road') {
      this.hoverSnap = snapToRoads(s.doc, s.derived, p[0], p[1], SNAP_PX * this.pxm());
      this.cursor = this.hoverSnap ? [this.hoverSnap.x, this.hoverSnap.y] : p;
    } else if (DRAW_TOOLS.has(tool)) {
      this.cursor = p;
    } else if (tool === 'delete' || tool === 'cut' || tool === 'join') {
      const h = hitObject(s.doc, s.derived, p[0], p[1], this.pxm(), tool === 'delete' ? undefined : 'road');
      this.hover = h?.type === 'object' ? h.sel : null;
      this.cutAt = tool === 'cut' && this.hover?.kind === 'road' ? cutLine(s.derived, this.hover.id, p) : null;
    }
    this.onChange();
  }

  pointerUp(): void {
    const g = this.gesture;
    this.gesture = null;
    if (!g || g.kind === 'pan' || g.kind === 'press') return;
    const s = this.store.getState();
    // roads that moved: trim habitats off them when "Habitats follow paths" is on
    let roads: RoadId[] = [];
    if (g.kind === 'move' && g.target.kind === 'road') roads = [g.target.id];
    if (g.kind === 'handle' && g.handle.kind === 'node') {
      const nodeId = g.handle.id;
      roads = Object.keys(s.doc.roads).filter((id) => s.doc.roads[id]?.nodes.includes(nodeId));
    }
    if (g.kind === 'width' && g.roadId) roads = [g.roadId];
    let trim: TrimResult | null = null;
    const changed = this.store.endDrag(s.follow && roads.length ? (d: PlanDoc) => {
      trim = trimAround(d, roads.filter((id) => d.roads[id]), speciesList(this.store));
    } : undefined);
    if (changed) this.store.say(trimMessage(trim));
  }

  doubleClick(e: MouseEvent): void {
    const s = this.store.getState();
    if (DRAW_TOOLS.has(s.tool)) { this.finish(); return; }
    if (s.tool !== 'select' || !s.selection) return;
    const p = this.mapPt(e), sel = s.selection, tol = 12 * this.pxm();
    let res: OpResult<unknown> | null = null;
    if (sel.kind === 'area' || sel.kind === 'plot') {
      const ring = sel.kind === 'plot' ? s.derived.plotRing : s.derived.areaRings[sel.id];
      if (!ring || nearestOnPoly([...ring, ring[0]!], p[0], p[1]).d > tol) return;
      res = this.store.apply(insertVert, { ring: sel.kind === 'plot' ? 'plot' as const : { area: sel.id }, x: p[0], y: p[1] }, { label: 'Add corner' });
    } else if (sel.kind === 'road') {
      const poly = s.derived.roadPolys[sel.id];
      if (!poly) return;
      const q = nearestOnPoly(poly.pts, p[0], p[1]);
      if (q.d > Math.max(tol, lerpAt(poly.widths, q.i, q.t) / 2)) return;
      res = this.store.apply(insertNode, { roadId: sel.id, x: p[0], y: p[1], onLine: true }, { label: 'Add dot' });
    } else if (sel.kind === 'river') {
      const poly = s.derived.riverPolys[sel.id], rv = s.doc.rivers[sel.id];
      if (!poly || !rv || nearestOnPoly(poly, p[0], p[1]).d > Math.max(tol, rv.w / 2)) return;
      res = this.store.apply(insertRiverPoint, { riverId: sel.id, x: p[0], y: p[1] }, { label: 'Add river point' });
    }
    if (res && !res.ok) toast(res.reason, 'error');
  }

  wheel(e: WheelEvent): void {
    e.preventDefault();
    const s = this.store.getState();
    this.store.setView(zoomAround(this.transform(), s.doc.size, s.view.zoom, Math.pow(1.0018, -e.deltaY), e.offsetX, e.offsetY));
  }

  /** Zooms by f around the canvas centre (toolbar buttons, + and − keys). */
  zoomBy(f: number): void {
    const s = this.store.getState();
    this.store.setView(zoomAround(this.transform(), s.doc.size, s.view.zoom, f, this.w / 2, this.h / 2));
  }

  /** Drop of a tray habitat at canvas position (sx, sy). */
  dropHabitat(sx: number, sy: number, item: { id: string; speciesId: string; count: number }): void {
    const p = toMap(this.transform(), sx, sy);
    placeHabitat(this.store, item.speciesId, item.count, p[0], p[1], item.id);
  }

  pointerLeave(): void {
    this.pointer = null;
    this.hover = null;
    this.hoverSnap = null;
    this.cutAt = null;
    this.onChange();
  }

  /** Finishes the drawing (double-click or Enter). */
  finish(): void {
    const s = this.store.getState();
    const raw = this.points, snaps = this.snaps;
    this.points = [];
    this.snaps = [];
    this.cursor = null;
    // drop points closer than 2 m to the previous one (double-click leftovers), keeping their snaps
    const pts: Pt[] = [], sn: (RoadSnapHit | null)[] = [];
    raw.forEach((q, i) => {
      const prev = pts[pts.length - 1];
      if (prev && Math.hypot(q[0] - prev[0], q[1] - prev[1]) <= 2) { if (snaps[i]) sn[sn.length - 1] = snaps[i] ?? null; return; }
      pts.push(q);
      sn.push(snaps[i] ?? null);
    });
    this.onChange();
    const tool = s.tool;
    if (tool === 'road' || tool === 'river') {
      if (pts.length < 2) { this.store.say(`A ${tool === 'road' ? 'path' : 'river'} needs at least 2 points.`); return; }
      if (tool === 'river') {
        const res = this.store.apply(addRiver, { points: pts }, { label: 'Add river' });
        if (res.ok) { this.store.select({ kind: 'river', id: res.value }); this.store.setTool('select'); }
        return;
      }
      // only the two ends join other roads; points in between stay free
      const ends = sn.map((x, i) => (i === 0 || i === pts.length - 1 ? x : null));
      let trim: TrimResult | null = null, id: RoadId | null = null;
      const res = this.store.apply((d) => {
        const r = addRoad(d, { points: pts, snaps: ends.map((x) => (x ? { roadId: x.roadId, ...(x.nodeId ? { nodeId: x.nodeId } : {}) } : null)) });
        if (r.ok) {
          id = r.value;
          if (s.follow) trim = trimAround(d, [r.value], speciesList(this.store));
        }
        return r;
      }, undefined, { label: 'Add path' });
      if (!res.ok) { toast(res.reason, 'error'); return; }
      if (id && this.store.getState().doc.roads[id]) this.store.select({ kind: 'road', id });
      this.store.setTool('select');
      this.store.say(trimMessage(trim, 'the new path'));
      return;
    }
    if (pts.length < 3) { this.store.say('Click at least 3 points to make a shape.'); return; }
    if (tool === 'boundary') {
      const res = this.store.apply(setPlot, { points: pts, smooth: s.doc.plot?.smooth ?? true }, { label: 'New boundary' });
      if (!res.ok) { toast(res.reason, 'error'); return; }
      this.store.setTool('select');
      this.store.select({ kind: 'plot' });
      this.store.say('New zoo boundary drawn. Re-arrange habitats to fit them inside it.');
      return;
    }
    if (tool === 'habitat' || tool === 'lake') {
      const sp = tool === 'habitat' ? s.species?.find((q) => q.id === s.toolSpecies) : undefined;
      if (tool === 'habitat' && !s.toolSpecies) return;
      const res = this.store.apply(addArea, tool === 'lake'
        ? { points: pts, kind: 'lake' as const }
        : { points: pts, kind: sp?.kind ?? 'habitat', speciesId: s.toolSpecies ?? '', count: sp?.groupSize.typical ?? 2 },
      { label: tool === 'lake' ? 'Add lake' : 'Add habitat' });
      if (!res.ok) { toast(res.reason, 'error'); return; }
      this.store.select({ kind: 'area', id: res.value });
      this.store.setTool('select');
    }
  }

  /** Removes the last placed point (Backspace while drawing). */
  undoPoint(): void {
    this.points.pop();
    this.snaps.pop();
    this.onChange();
  }
}

/** A short line across the road at the spot nearest p, as wide as the gap a cut leaves. */
function cutLine(derived: Derived, roadId: string, p: Pt): { a: Pt; b: Pt } | null {
  const poly = derived.roadPolys[roadId];
  if (!poly || poly.pts.length < 2) return null;
  const q = nearestOnPoly(poly.pts, p[0], p[1]), hw = lerpAt(poly.widths, q.i, q.t) / 2 + 2;
  return { a: [q.px + q.nx * hw, q.py + q.ny * hw], b: [q.px - q.nx * hw, q.py - q.ny * hw] };
}
